import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, FileEdit, CheckCircle, XCircle, Clock, RefreshCw, ChevronRight, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const EditPermissionNotifications = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [validHours, setValidHours] = useState("24");
  const [processing, setProcessing] = useState(false);

  // Check if user is an approver
  const approverRoles = ['director', 'managing_director', 'clinical_lead', 'lead_therapist'];
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  const isApprover = isSuperAdmin || approverRoles.includes(user?.role);

  const fetchNotifications = useCallback(async () => {
    if (!isApprover) {
      setLoading(false);
      return;
    }
    
    try {
      // Fetch general notifications (includes edit permission requests)
      const response = await api.get('/auth/notifications');
      const editPermNotifs = (response.data || []).filter(
        n => n.type === 'edit_permission_request'
      );
      setNotifications(editPermNotifs);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [api, isApprover]);

  useEffect(() => {
    fetchNotifications();
    // Refresh every 2 minutes
    const interval = setInterval(fetchNotifications, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/auth/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleApprove = async () => {
    if (!selectedNotification?.request_id) return;
    
    setProcessing(true);
    try {
      await api.post('/edit-permissions/approve', {
        request_id: selectedNotification.request_id,
        valid_hours: parseInt(validHours)
      });
      
      // Mark notification as read
      await markAsRead(selectedNotification.id);
      
      toast.success(`Edit permission approved for ${validHours} hours`);
      setShowApprovalDialog(false);
      setSelectedNotification(null);
      fetchNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedNotification?.request_id) return;
    
    setProcessing(true);
    try {
      await api.post(`/edit-permissions/reject?request_id=${selectedNotification.request_id}`);
      
      // Mark notification as read
      await markAsRead(selectedNotification.id);
      
      toast.success('Edit permission request rejected');
      setShowApprovalDialog(false);
      setSelectedNotification(null);
      fetchNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  // Don't render if user is not an approver
  if (!isApprover) {
    return null;
  }

  const unreadCount = notifications.length;

  // Don't show badge if no notifications
  if (unreadCount === 0 && !open) {
    return null;
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative"
            data-testid="edit-permission-notifications-btn"
          >
            <Shield className="h-5 w-5 text-amber-600" />
            {unreadCount > 0 && (
              <Badge 
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-amber-500 text-white text-xs animate-pulse"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="end">
          <div className="p-3 border-b bg-amber-50">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                <FileEdit className="w-4 h-4" />
                Edit Permission Requests
              </h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => fetchNotifications()}
                className="h-8 w-8 p-0"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            {unreadCount > 0 && (
              <p className="text-xs text-amber-600 mt-1">
                {unreadCount} pending request{unreadCount > 1 ? 's' : ''} awaiting your approval
              </p>
            )}
          </div>
          
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-slate-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                <p className="font-medium">No pending requests</p>
                <p className="text-sm">All edit permission requests have been processed</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notif) => (
                  <div 
                    key={notif.id}
                    className="p-3 hover:bg-amber-50/50 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedNotification(notif);
                      setShowApprovalDialog(true);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">
                          {notif.requester_name}
                        </p>
                        <p className="text-xs text-slate-500 capitalize">
                          {notif.requester_role?.replace(/_/g, ' ')} • Edit access for {notif.patient_name}
                        </p>
                        <p className="text-xs text-slate-400 mt-1 line-clamp-1">
                          Reason: {notif.reason}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-slate-400">
                            {formatDate(notif.created_at)} at {formatTime(notif.created_at)}
                          </span>
                          <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                            <Clock className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="p-2 border-t bg-slate-50">
            <Button 
              variant="ghost" 
              className="w-full justify-between text-sm"
              onClick={() => {
                setOpen(false);
                navigate('/patients');
              }}
            >
              View all in Patients page
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <FileEdit className="w-5 h-5" />
              Edit Permission Request
            </DialogTitle>
            <DialogDescription>
              Review and approve or reject this edit permission request
            </DialogDescription>
          </DialogHeader>
          
          {selectedNotification && (
            <div className="py-4 space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{selectedNotification.requester_name}</p>
                    <p className="text-xs text-slate-500 capitalize">
                      {selectedNotification.requester_role?.replace(/_/g, ' ')}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Patient:</span>
                    <span className="font-medium">{selectedNotification.patient_name}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Reason:</span>
                    <p className="mt-1 p-2 bg-white rounded border text-slate-700">
                      {selectedNotification.reason}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Permission Duration</label>
                <Select value={validHours} onValueChange={setValidHours}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 hour</SelectItem>
                    <SelectItem value="4">4 hours</SelectItem>
                    <SelectItem value="8">8 hours</SelectItem>
                    <SelectItem value="24">24 hours (default)</SelectItem>
                    <SelectItem value="48">48 hours</SelectItem>
                    <SelectItem value="72">72 hours</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  After this time, the user will need to request permission again
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              className="border-red-200 text-red-600 hover:bg-red-50"
              onClick={handleReject}
              disabled={processing}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleApprove}
              disabled={processing}
            >
              {processing ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditPermissionNotifications;
